import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { useToast } from '@aimsos/ui';

import { startNextBestActionWorkflow } from '../../../services/nextBestAction.js';

/**
 * "Start workflow" from a Next Best Action (CT-5713).
 *
 * One request per double click (`submittingRef`). On success: a toast
 * `{ title, message }` — "<person> added to the Renewal playbook" / "Step 1 of
 * 5: …" — then `onStarted` (the page closes the slide-out and re-reads the
 * suggestions and the header chip). On failure: an error toast, and the caller
 * keeps the slide-out open.
 *
 * @param {object} options
 * @param {string} options.entityType
 * @param {string} options.entityId
 * @param {string} options.entityName — for the toast.
 * @param {() => void} [options.onStarted]
 */
export function useStartNextBestActionWorkflow({ entityType, entityId, entityName, onStarted } = {}) {
  const { t } = useTranslation('unified-profiles');
  const toast = useToast();
  const [starting, setStarting] = useState(false);
  const submittingRef = useRef(false);
  // The record open NOW. The page stays mounted across records: a start that
  // answers after the user moved on still toasts (it did start, on its record),
  // but its follow-up — re-reading that record — must not run on this one.
  const recordKey = `${entityType}/${entityId}`;
  const openRecordRef = useRef(recordKey);
  useEffect(() => {
    openRecordRef.current = recordKey;
  }, [recordKey]);

  const start = useCallback(
    async (item) => {
      if (submittingRef.current || !item) return false;
      submittingRef.current = true;
      setStarting(true);
      const startedOn = `${entityType}/${entityId}`;

      try {
        const { body } = await startNextBestActionWorkflow({ entityType, entityId, itemId: item.id });
        const workflow = body?.workflow ?? {};
        toast.success({
          title: t('profile.nba.toast.startedTitle', { name: entityName, workflow: workflow.name ?? item.workflow?.name }),
          message: workflow.message ?? t('profile.nba.toast.startedMessage', { step: workflow.step, total: workflow.total }),
        });
        if (openRecordRef.current === startedOn) onStarted?.();
        return true;
      } catch (err) {
        toast.error({
          title: t('profile.nba.toast.startErrorTitle'),
          message: err?.message ?? t('profile.nba.toast.startErrorMessage'),
        });
        return false;
      } finally {
        submittingRef.current = false;
        setStarting(false);
      }
    },
    [entityType, entityId, entityName, onStarted, toast, t],
  );

  return useMemo(() => ({ start, starting }), [start, starting]);
}

export default useStartNextBestActionWorkflow;
